import { useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@acme/ui/button";
import { Checkbox } from "@acme/ui/checkbox";
import { Field, FieldContent, FieldLabel } from "@acme/ui/field";
import { Label } from "@acme/ui/label";
import { Progress } from "@acme/ui/progress";
import {
  CW_NOTIFICATION_TYPES_FOR_DEV_TEST,
  EVENT_BASED_NOTIFICATION_TYPES,
  SIMULATED_WAR_DURATION_MS,
  SIMULATION_SPEED_FACTOR,
  TIME_BASED_NOTIFICATION_TYPES,
} from "../_constants/table-of-truth.js";
import type { CwNotificationType } from "@acme/db/schema";
import type { ConnectionStatus } from "@acme/db/schema";
import type { RunCwSimulationStreamChunk } from "@acme/validators-shared/routers/clan-war/runCwSimulationStream.schema";
import { trpcClient } from "~/common/utils/trpc.js";

/** Formatiert verbleibende Zeit als HH:MM:SS (24h-Simulation). */
function formatSimulatedTimeRemaining(ms: number): string
{
  const totalSec = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSec / 3600);
  const min = Math.floor((totalSec % 3600) / 60);
  const sec = totalSec % 60;
  return `${hours.toString().padStart(2, "0")}:${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
}

interface CwNotificationDevTestTabProps
{
  readonly playerTag: string;
  readonly clanTag: string;
  /** Connection status used for simulation (toggle in UI). */
  readonly simulatedConnectionStatus: ConnectionStatus;
  readonly onSimulatedConnectionStatusChange: (status: ConnectionStatus) => void;
  readonly selectedTypes: Set<CwNotificationType>;
  readonly onSelectedTypesChange: (next: Set<CwNotificationType>) => void;
  readonly myAttacksLeft: number;
  readonly onMyAttacksLeftChange: (n: number) => void;
  readonly onRunTestSelected: () => void;
  readonly onRunTestAll: () => void;
  readonly isRunning: boolean;
  readonly progressTotalMs: number;
  readonly progressElapsedMs: number;
  readonly displaySimulatedRemainingMs: number;
}

export function CwNotificationDevTestTab({
  playerTag,
  clanTag,
  simulatedConnectionStatus,
  onSimulatedConnectionStatusChange,
  selectedTypes,
  onSelectedTypesChange,
  myAttacksLeft,
  onMyAttacksLeftChange,
  onRunTestSelected,
  onRunTestAll,
  isRunning,
  progressTotalMs,
  progressElapsedMs,
  displaySimulatedRemainingMs,
}: CwNotificationDevTestTabProps)
{
  const { t } = useTranslation();

  const toggle = useCallback(
    (type: CwNotificationType) =>
    {
      const next = new Set(selectedTypes);
      if (next.has(type))
      {
        next.delete(type);
      }
      else
      {
        next.add(type);
      }
      onSelectedTypesChange(next);
    },
    [selectedTypes, onSelectedTypesChange]
  );

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold">{t("cwNotifications.devTestTitle", "Dev: Notifications testen")}</h3>

      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">
          {t(
            "cwNotifications.devTestDescription",
            "Wähle die zu testenden Benachrichtigungstypen. Der Countdown zeigt die echte 24h-Restzeit, läuft aber ~1200× schneller (1,2 min reale Laufzeit)."
          )}
        </p>

        <div className="space-y-2">
          <FieldLabel>{t("cwNotifications.timeBasedNotifications")}</FieldLabel>
          <div className="flex flex-wrap gap-4">
            {TIME_BASED_NOTIFICATION_TYPES.map((type) => (
              <Field key={type} orientation="horizontal" className="flex items-center gap-2">
                <Checkbox
                  id={`dev-${type}`}
                  checked={selectedTypes.has(type)}
                  onCheckedChange={() => toggle(type)}
                  disabled={isRunning}
                />
                <FieldLabel htmlFor={`dev-${type}`} className="font-normal cursor-pointer">
                  {type.replace("_", " ")}
                </FieldLabel>
              </Field>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel>{t("cwNotifications.eventBasedNotifications")}</FieldLabel>
          <div className="flex flex-wrap gap-4">
            {EVENT_BASED_NOTIFICATION_TYPES.map((type) => (
              <Field key={type} orientation="horizontal" className="flex items-center gap-2">
                <Checkbox
                  id={`dev-${type}`}
                  checked={selectedTypes.has(type)}
                  onCheckedChange={() => toggle(type)}
                  disabled={isRunning || simulatedConnectionStatus !== "connected"}
                />
                <FieldLabel htmlFor={`dev-${type}`} className="font-normal cursor-pointer">
                  {type.replace(/_/g, " ")}
                </FieldLabel>
              </Field>
            ))}
          </div>
          {simulatedConnectionStatus !== "connected" && (
            <p className="text-muted-foreground text-xs">{t("cwNotifications.advancedRequiresVerification")}</p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Label htmlFor="dev-connection-status" className="text-sm">
              {t("cwNotifications.simulatedConnectionStatus", "Simulation: Verbindungsstatus")}
            </Label>
            <select
              id="dev-connection-status"
              value={simulatedConnectionStatus}
              onChange={(e) => onSimulatedConnectionStatusChange(e.target.value as ConnectionStatus)}
              disabled={isRunning}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm">
              <option value="connection_requested">connection_requested</option>
              <option value="connected">connected</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="dev-my-attacks">{t("cwNotifications.myAttacksLeft", "My attacks left")}</Label>
            <select
              id="dev-my-attacks"
              value={myAttacksLeft}
              onChange={(e) => onMyAttacksLeftChange(Number(e.target.value))}
              disabled={isRunning}
              className="rounded-md border border-input bg-background px-2 py-1 text-sm">
              {[0, 1, 2].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {(progressTotalMs > 0 || isRunning) && (
        <div className="space-y-3 rounded-lg border bg-muted/30 p-4">
          <div className="space-y-2">
            <Progress value={progressTotalMs > 0 ? (progressElapsedMs / progressTotalMs) * 100 : 0} className="h-3" />
            <div
              className="flex justify-center text-sm font-semibold tabular-nums tracking-wide text-foreground"
              aria-hidden>
              {progressTotalMs > 0
                ? t("cwNotifications.timeRemaining", "{{time}}", {
                    time: formatSimulatedTimeRemaining(
                      displaySimulatedRemainingMs > 0
                        ? displaySimulatedRemainingMs
                        : Math.max(0, SIMULATED_WAR_DURATION_MS - progressElapsedMs * SIMULATION_SPEED_FACTOR)
                    ),
                  })
                : isRunning
                  ? t("cwNotifications.preparing", "Preparing…")
                  : null}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="default"
          onClick={onRunTestSelected}
          disabled={isRunning || selectedTypes.size === 0}>
          {t("cwNotifications.testBasedOnSelection", "Test based on selection")}
        </Button>
        <Button type="button" variant="outline" onClick={onRunTestAll} disabled={isRunning}>
          {t("cwNotifications.testAll", "Test all")}
        </Button>
      </div>
    </div>
  );
}

export function useCwDevTestRunner({
  playerTag,
  clanTag,
  connectionStatus,
  selectedTypes,
  myAttacksLeft,
}: {
  playerTag: string;
  clanTag: string;
  connectionStatus: ConnectionStatus;
  selectedTypes: Set<CwNotificationType>;
  myAttacksLeft: number;
})
{
  const [isRunning, setIsRunning] = useState(false);
  const [progressTotalMs, setProgressTotalMs] = useState(0);
  const [progressElapsedMs, setProgressElapsedMs] = useState(0);
  /** Anzeige: simulierte Restzeit in ms, aktualisiert nur wenn sich die angezeigte Sekunde ändert (24:00:00 → 23:59:59 → …). */
  const [displaySimulatedRemainingMs, setDisplaySimulatedRemainingMs] = useState(0);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const totalMsRef = useRef<number>(0);
  const lastDisplayedSecondRef = useRef<number>(-1);
  const lastProgressElapsedMsRef = useRef<number>(0);

  const runStream = useCallback(
    async (types: CwNotificationType[]) =>
    {
      setIsRunning(true);
      setProgressElapsedMs(0);
      setProgressTotalMs(0);
      setDisplaySimulatedRemainingMs(0);
      lastDisplayedSecondRef.current = -1;
      try
      {
        const iterable = (await trpcClient.clanWar.runCwSimulationStream.query({
          playerTag,
          clanTag,
          connectionStatus,
          selectedTypes: types,
          myAttacksLeft,
        })) as AsyncIterable<RunCwSimulationStreamChunk>;
        for await (const chunk of iterable)
        {
          if (chunk.event === "start")
          {
            totalMsRef.current = chunk.progressTotalMs;
            startTimeRef.current = Date.now();
            setProgressTotalMs(chunk.progressTotalMs);
            setDisplaySimulatedRemainingMs(SIMULATED_WAR_DURATION_MS);
            lastDisplayedSecondRef.current = Math.floor(SIMULATED_WAR_DURATION_MS / 1000);
            lastProgressElapsedMsRef.current = 0;
            if (countdownIntervalRef.current)
            {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            countdownIntervalRef.current = setInterval(
              () =>
              {
                const elapsed = Math.min(Date.now() - startTimeRef.current, totalMsRef.current);
                if (elapsed - lastProgressElapsedMsRef.current >= 50)
                {
                  lastProgressElapsedMsRef.current = elapsed;
                  setProgressElapsedMs(elapsed);
                }
                const simulatedRemainingMs = Math.max(0, SIMULATED_WAR_DURATION_MS - elapsed * SIMULATION_SPEED_FACTOR);
                const currentSecond = Math.floor(simulatedRemainingMs / 1000);
                if (currentSecond !== lastDisplayedSecondRef.current)
                {
                  lastDisplayedSecondRef.current = currentSecond;
                  setDisplaySimulatedRemainingMs(simulatedRemainingMs);
                }
              },
              Math.max(2, 1000 / SIMULATION_SPEED_FACTOR)
            );
          }
          else if (chunk.event === "checkpoint")
          {
            setProgressElapsedMs(chunk.progressElapsedMs);
            const simulatedRemainingMs = Math.max(
              0,
              SIMULATED_WAR_DURATION_MS - chunk.progressElapsedMs * SIMULATION_SPEED_FACTOR
            );
            lastDisplayedSecondRef.current = Math.floor(simulatedRemainingMs / 1000);
            setDisplaySimulatedRemainingMs(simulatedRemainingMs);
          }
          else if (chunk.event === "done")
          {
            break;
          }
        }
      }
      finally
      {
        if (countdownIntervalRef.current)
        {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }
        setIsRunning(false);
        setProgressTotalMs(0);
        setProgressElapsedMs(0);
        setDisplaySimulatedRemainingMs(0);
      }
    },
    [playerTag, clanTag, connectionStatus, myAttacksLeft]
  );

  const runTestSelected = useCallback(async () =>
  {
    if (selectedTypes.size === 0) return;
    await runStream(Array.from(selectedTypes));
  }, [selectedTypes, runStream]);

  const runTestAll = useCallback(async () =>
  {
    await runStream(CW_NOTIFICATION_TYPES_FOR_DEV_TEST);
  }, [runStream]);

  return {
    runTestSelected,
    runTestAll,
    isRunning,
    progressTotalMs,
    progressElapsedMs,
    displaySimulatedRemainingMs,
  };
}
